import { create } from 'zustand'

type Store = {
  audioUrl: string | null
  setAudioUrl: (audioUrl: string | null) => void
  audioRef: HTMLAudioElement | null
  setAudioRef: (audioRef: HTMLAudioElement | null) => void
  fastForward: (seconds: number) => void
  rewind: (seconds: number) => void
  setAudioTime: (seconds: number) => void
  play: () => void
  pause: () => void
  setAudioPlaybackRate: (rate: number) => void
  setAudioVolume: (volume: number) => void
  isPlaying: boolean
  progress: number
  duration: number
  togglePlay: () => void
  cleanupAudio: () => void
  isVisible: boolean
  setIsVisible: (isVisible: boolean) => void
}

export const useAudioStore = create<Store>((set, get) => ({
  audioUrl: null,
  isPlaying: false,
  progress: 0,
  duration: 0,
  audioRef: null,
  isVisible: true,

  setAudioUrl: (audioUrl) => {
    const { audioRef, cleanupAudio } = get()

    if (audioRef) {
      cleanupAudio()
    }

    if (audioUrl) {
      const newAudio = new Audio(audioUrl)

      newAudio.addEventListener('loadedmetadata', () => {
        set({ duration: newAudio.duration })
      })

      newAudio.volume = 0.5
      newAudio.addEventListener('play', () => set({ isPlaying: true }))
      newAudio.addEventListener('pause', () => set({ isPlaying: false }))
      newAudio.addEventListener('timeupdate', () => {
        set({ progress: newAudio.currentTime })
      })

      set({ audioUrl, audioRef: newAudio })
    } else {
      cleanupAudio()
      set({
        audioUrl: null,
        isPlaying: false,
        progress: 0,
        duration: 0,
      })
    }
  },

  setAudioRef: (audioRef) => set({ audioRef }),
  togglePlay: () => {
    const { audioRef, isPlaying } = get()
    if (!audioRef) return

    if (isPlaying) {
      audioRef.pause()
    } else {
      audioRef.play()
    }
  },
  fastForward: (seconds) => {
    const audioRef = get().audioRef
    if (!audioRef) return
    audioRef.currentTime += seconds
  },
  rewind: (seconds) => {
    const audioRef = get().audioRef
    if (!audioRef) return
    audioRef.currentTime -= seconds
  },
  setAudioTime: (seconds) => {
    const audioRef = get().audioRef
    if (!audioRef) return
    audioRef.currentTime = seconds
  },
  play: () => {
    const audioRef = get().audioRef
    if (!audioRef) return
    audioRef.play()
  },
  pause: () => {
    const audioRef = get().audioRef
    if (!audioRef) return
    audioRef.pause()
  },
  setAudioPlaybackRate: (rate) => {
    const audioRef = get().audioRef
    if (!audioRef) return
    audioRef.playbackRate = rate
  },
  setAudioVolume: (volume) => {
    const audioRef = get().audioRef
    if (!audioRef) return
    audioRef.volume = volume
  },

  cleanupAudio: () => {
    const { audioRef } = get()
    if (audioRef) {
      audioRef.pause()
      audioRef.src = '' // Remove source to clean up memory
      audioRef.load()
    }
    set({ audioRef: null, isPlaying: false, progress: 0, duration: 0 })
  },

  setIsVisible: (isVisible) => {
    set({ isVisible })
    if (!isVisible) {
      const { cleanupAudio } = get()
      cleanupAudio()
    }
  },
}))
